import {
  getMediaBasePath,
  IQuestionStatus,
  IQuestionStatusObject,
} from '@geneo2-web/shared-ui';
import { Subject_SubjectEnum } from '@protos/common/common.db_pb';
import {
  MultiSubjectSection,
  TaskInfo,
} from '@protos/learning_management/lms.db_pb';
import { TeachClassSubjects } from '@protos/learning_management/lms.lesson.common.apis_pb';
import { SubjectList } from '@protos/user_management/ums.login.apis_pb';

const SubjectMap = {
  M: 'Mathematics',
  E: 'English',
  S: 'Science',
  SS: 'Social Science',
  SP: 'Physics',
  SC: 'Chemistry',
  SB: 'Biology',
  ELIT: 'English Literature',
  ELANG: 'English Language',
  EG: 'English Grammar',
  HIST: 'History',
  GEOG: 'Geography',
  ICT: 'Information and Communication Technology',
  EVS: 'Environmental Studies',
  CIV: 'Civics',
  ECO: 'Economics',
  GK: 'General Knowledge',
  BIZ: 'Business Studies',
  ACCT: 'Accountancy',
  STATS: 'Statistics',
  LR: 'Logical Reasoning',
  VOCAB: 'Vocabulary',
  GAME: 'Games',
  APT: 'Aptitude',
  GS: 'General Studies',
  LANG: 'Foreign Language',
  ENTMT: 'Entertainment',
  SBI_PO: 'SBI PO Exam',
  IBPS_PO: 'IBPS PO Exam',
  BANK_PO: 'Bank PO Exam',
  GOVT: 'Government Exams',
  URD: 'Urdu',
  H: 'Hindi',
  SANS: 'Sanskrit',
  MAR: 'Marathi',
  GUJ: 'Gujarati',
  SPL: 'Social and Political Life',
  PS: 'Political Science',
  TRAINING: 'Training',
  CL: 'Climate Leadership',
  ROBO: 'Robotics',
  COMP: 'Computer',
};
export const getSubjectNameFromEnum = (
  subjectEnum?: Subject_SubjectEnum
): string => {
  if (subjectEnum === undefined || subjectEnum === null) {
    return '';
  }
  if (subjectEnum === Subject_SubjectEnum.UNDEFINED) {
    return '';
  }
  const subjectKey = Object.keys(Subject_SubjectEnum).find(
    (key) =>
      Subject_SubjectEnum[key as keyof typeof Subject_SubjectEnum] ===
      subjectEnum
  );
  if (!subjectKey) return '';
  return SubjectMap[subjectKey as keyof typeof SubjectMap] ?? subjectKey;
};

// =============================================================

export const getSubjectImageBySubjectId = (
  subjectId?: number,
  classSubjects?: SubjectList[]
): string => {
  if (subjectId == null || !classSubjects?.length) return '';
  for (const subjectData of classSubjects ?? []) {
    if (subjectData.subjectId === subjectId && subjectData.iconUrl) {
      return getMediaBasePath(
        subjectData.iconUrl,
        'schoolnetStaticAssetBucket'
      );
    }
  }
  return '';
};

// =============================================================

export const getSubjectColorBySubjectId = (
  subjectId?: number,
  classSubjects?: SubjectList[]
): string => {
  if (subjectId == null || !classSubjects?.length) return '';
  for (const subjectData of classSubjects ?? []) {
    if (subjectData.subjectId === subjectId && subjectData.iconUrl) {
      return `${subjectData.textColor || '#000000'}33`;
    }
  }
  return '';
};

export interface ISummaryData {
  subject: string;
  attempted: number;
  total: number;
}

export const getSummaryData = (
  sections: MultiSubjectSection[],
  sectionRanges: { start: number; end: number }[],
  questionStatusArray: IQuestionStatusObject[]
): ISummaryData[] => {
  return sections.map((section, index) => {
    const range = sectionRanges[index];
    if (!range) {
      return {
        subject: getSubjectNameFromEnum(section.subject),
        attempted: 0,
        total: 0,
      };
    }
    const sectionQuestions = questionStatusArray.slice(range.start, range.end);
    const total = sectionQuestions.length;
    const attempted = sectionQuestions.filter(
      (q) =>
        q.statusInfo === IQuestionStatus.Attempted ||
        q.statusInfo === IQuestionStatus.Correct ||
        q.statusInfo === IQuestionStatus.Incorrect
    ).length;

    return {
      subject: getSubjectNameFromEnum(section.subject),
      attempted,
      total,
    };
  });
};

// =============================================================

/** Sort sequence items and return only defined question ids. */
const toSortedQuestionIds = (
  items: { questionId?: string; sequence: number }[]
): string[] =>
  items
    .slice()
    .sort((a, b) => a.sequence - b.sequence)
    .map((item) => item.questionId)
    .filter((id): id is string => Boolean(id));

/**
 * Resolve question order for Answer & Explanations.
 * OMR: match setName → omrSetInfo, then section by subjectId (or first section).
 * Otherwise: multi-subject subsection meta, or taskQuestionInfo.
 */
export const getSortedQuestionIdsForReview = (params: {
  questionsSequenceInfo?: TaskInfo;
  isOmrAssessment: boolean;
  setName?: string;
  selectedSectionNo?: number;
}): string[] => {
  const { questionsSequenceInfo, isOmrAssessment, setName, selectedSectionNo } =
    params;

  if (isOmrAssessment) {
    const resolvedSetName = setName || 'A'; // If setName is missing during OMR rendering, default to Set A.
    const selectedOmrSet = questionsSequenceInfo?.omrSetInfo?.find(
      (set) => set.omrSetName === resolvedSetName
    );
    if (!selectedOmrSet?.omrSectionInfo?.length) return [];

    const multiSubjectSections =
      questionsSequenceInfo?.multiSubjectSections ?? [];
    const selectedSection = multiSubjectSections.find(
      (section) => section.sectionNo === selectedSectionNo
    );
    const omrSections = selectedOmrSet.omrSectionInfo;
    const omrSection = selectedSection
      ? omrSections.find((s) => s.subjectId === selectedSection.subjectId) ??
        omrSections[0]
      : omrSections[0];

    return toSortedQuestionIds(omrSection?.omrQuestionsSequenceInfo ?? []);
  }

  if (selectedSectionNo != null) {
    const sectionQuestions =
      questionsSequenceInfo?.multiSubjectSections
        ?.find((section) => section.sectionNo === selectedSectionNo)
        ?.multiSubjectSubSections.map(
          (subSection) => subSection.subSectionMetaData
        )
        .flat() ?? [];
    return toSortedQuestionIds(sectionQuestions);
  }

  return toSortedQuestionIds(questionsSequenceInfo?.taskQuestionInfo ?? []);
};

// =============================================================

export const CLICKER_CLASSROOM_POPUP_LINES = [
  'This is a clicker-based assessment that can only be initiated by the teacher within a classroom setting.',
  'Once the Assessment Ended You can come and see the results',
];

export const OMR_CLASSROOM_POPUP_LINES = [
  'This is an OMR-based classroom assessment. Fill and darken the bubble for your chosen answer carefully.',
  'After completing the assessment, submit your OMR sheet for evaluation. Once evaluated, you can come back here to view your test report.',
];
export const LIVE_CLASSROOM_POPUP_LINES = [
  'This is a teacher-led live quiz — join it from Home once your teacher starts it.',
  'Once the Assessment Ended You can come and see the results',
];
// =============================================================
